import { Button } from "@/components/ui/button";
import { ArForm, ArFormField } from "@/v2/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import LoadingAnimation from "../../ui/LoadingAnimation";
import {
    CreateUserSchema,
    UpdateUserSchema,
    UserFormSchema,
    UserFormSchemaWithoutRefine,
} from "@/v2/schema/members";
import FindOneUser from "@/v2/api/users/FindOne";
import UpdateUser from "@/v2/api/users/Update";
import { RolesComboBox } from "../../ui/combo-box/Roles";
import { ArPasswordFormField } from "../../ui/form/PasswordFormField";
import { ArEmployeeComboBox } from "../../ui/combo-box";
import CreateUser from "@/v2/api/users/Create";

export default function MembersForm({
    id,
    closeOnSave,
}: {
    id?: string;
    closeOnSave: () => void;
}) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ["users", id],
        queryFn: () => FindOneUser(id!),
        enabled: !!id,
    });

    const form = useForm<UserFormSchema>({
        resolver: zodResolver(UserFormSchema),
        defaultValues: {
            email: "",
            full_name: "",
            password: id ? undefined : "",
            confirmPassword: id ? undefined : "",
            phone_number: "",
            roles: [],
            is_admin: false,
            direct_permissions: [],
            assigned_employee_id: "",
        },
    });

    const { reset } = form;

    useEffect(() => {
        if (query.data) {
            reset({
                email: query.data.email || "",
                full_name: query.data.full_name || "",
                phone_number: query.data.phone_number || "",
                roles: query.data.roles?.map((r: any) => r.id) || [],
                is_admin: query.data.is_admin || false,
                assigned_employee_id: query?.data?.assigned_employee_id || "",
                direct_permissions: [],
            });
        }
    }, [query.data, reset]);

    const CreateMutation = useMutation({
        mutationFn: (data: CreateUserSchema) =>
            CreateUser({ ...data, password: data.password! }),
    });

    const EditMutation = useMutation({
        mutationFn: (data: UpdateUserSchema) => UpdateUser({ id: id!, data }),
    });

    const handleFormSubmit = (data: UserFormSchema) => {
        const mutation = id ? EditMutation : CreateMutation;
        const action = id ? "updated" : "created";

        mutation.mutate(data, {
            onError: (err) => {
                console.error({ err });
                toast.error("Something went wrong", {
                    description: `While ${action} the user, an issue occurred.`,
                });
            },
            onSuccess: () => {
                toast.success(`User successfully ${action}`);
                queryClient.invalidateQueries({
                    queryKey: ["users"],
                });
                closeOnSave();
            },
        });
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">
                        Failed to load user details
                    </p>
                    <Button onClick={closeOnSave}>Go Back</Button>
                </div>
            </div>
        );
    }

    return (
        <ArForm
            form={form}
            formSchema={UserFormSchemaWithoutRefine}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<UserFormSchema>
                        label="Full Name"
                        name="full_name"
                        placeholder="Enter full name"
                    />
                    <ArFormField<UserFormSchema>
                        label="Email"
                        name="email"
                        placeholder="Enter email address"
                    />
                </div>

                {!id && (
                    <>
                        <ArPasswordFormField<UserFormSchema>
                            label="Password"
                            name="password"
                            placeholder="Enter password"
                            infoText="Must be at least 8 characters, include uppercase, lowercase, number, and special character."
                        />

                        <ArPasswordFormField<UserFormSchema>
                            label="Confirm Password"
                            name="confirmPassword"
                            placeholder="Confirm password"
                            infoText="Re enter the same passowrd to confirm"
                        />
                    </>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<UserFormSchema>
                        label="Phone Number"
                        name="phone_number"
                        placeholder="Enter phone number"
                    />
                    <RolesComboBox<UserFormSchema>
                        name="roles"
                        defaultValue={query?.data?.roles?.[0] || null}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArEmployeeComboBox<UserFormSchema>
                        name="assigned_employee_id"
                        defaultValue={query?.data?.assigned_employee || null}
                    />
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update User"
                              : "Create User"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
